/**
 * Personal layer — Gössl 2026 (SJ 12.09.2026: "work through all recs").
 *
 * One small store for everything the storefront remembers about a visitor:
 *   a) size memory      — goessl_size     { "<family>": { num, alpha, sfx, t } }
 *   d) wishlist alerts  — goessl_wish_snap { "<handle>": { p, a, t, alert, at } }
 *   f) return visits    — goessl_last     { p:{h,title,img,url,t}, c:{url,title,t}, seen }
 *
 * Loaded in <head> (defer) right after goessl-2026.js, so product-form.js and
 * recently-viewed.js can rely on window.GoesslPersonal at start. Everything is
 * try/catch-guarded: a private window or blocked storage must never break a page.
 *
 * Size families keep the memory honest: a woman's 38 never lands on a men's
 * Janker, a hat size never on a Dirndl. Family = gender (f|m|k|u, from the
 * Frauen/Männer/Kinder tags) + kind (konf|lederhose|schuhe|hut|stutzen|
 * handschuh|guertel|schuerze, from the rule-order tags). Numeric and alpha sizes are
 * stored side by side, so "38" and "M" can both be remembered for one family.
 */
(function () {
  'use strict';

  var SIZE_KEY = 'goessl_size';
  var SIZE_SPLIT_KEY = 'goessl_size_kragen';   // r192: one-time split of the collar sizes, see splitCollarSizes
  var SNAP_KEY = 'goessl_wish_snap';
  var LAST_KEY = 'goessl_last';
  var SESSION_GAP_MS = 30 * 60 * 1000;   // a return visit = 30 min without activity
  var LAST_MAX_AGE_MS = 30 * 24 * 3600 * 1000;
  var ALERT_TTL_MS = 7 * 24 * 3600 * 1000;

  function readJson(key) {
    try { var v = JSON.parse(localStorage.getItem(key)); return (v && typeof v === 'object') ? v : {}; } catch (e) { return {}; }
  }
  function writeJson(key, v) { try { localStorage.setItem(key, JSON.stringify(v)); } catch (e) {} }

  // ── families ──────────────────────────────────────────────────────────────
  function genderOf(tags) {
    var t = (tags || []).join('|');
    if (/Kinder|Mädchen|Buben/i.test(t)) return 'k';
    if (/Frauen|Damen/i.test(t)) return 'f';
    if (/Männer|Herren/i.test(t)) return 'm';
    return 'u';
  }
  function kindOf(tags, type, title) {
    var t = ((tags || []).join('|') + '|' + (type || '') + '|' + (title || '')).toLowerCase();
    if (/handschuh/.test(t)) return 'handschuh';
    if (/schuh|haferl|stiefel(?!hose)/.test(t)) return 'schuhe';
    if (/\bhut\b|hüte|kappe|trachtenhut/.test(t)) return 'hut';
    if (/stutzen|socken|socks/.test(t)) return 'stutzen';
    if (/gürtel|guertel/.test(t)) return 'guertel';
    if (/lederhose/.test(t)) return 'lederhose';
    if (/schürze|schuerze/.test(t)) return 'schuerze';
    return 'konf';
  }
  // r160 (Frau Adler, Kundendienst, 14.09.2026): apron sizes map exactly onto the
  // Dirndl sizes — XS = 32–34, S = 36–38, M = 40–42, L = 44–46. Damen only, and only
  // Dirndl → apron: an apron M spans two Dirndl sizes, so nothing is derived back.
  var APRON_SIZE = { '32': 'XS', '34': 'XS', '36': 'S', '38': 'S', '40': 'M', '42': 'M', '44': 'L', '46': 'L' };
  function family(gender, kind) { return gender + ':' + kind; }
  // r192: men's shirts are sized by collar width (37 … 46), every other men's piece by Konfektion (44 … 64).
  // Sharing 'm:konf' turned a shirt's collar 44 into a preselected gilet 44 and the other way round, so
  // collar sizes have their own family 'm:kragen': a Schnitt named Kragenweite decides; without a Schnitt,
  // an odd size between 35 and 47 among the product's sizes, which no Konfektion scale has (live 05.10:
  // 88 men's pieces, all of them shirts and shirt-jackets). Women's families stay as they are.
  function oddCollar(value) {
    var c = parse(value).clean;
    if (!/^\d+$/.test(c)) return false;
    var n = parseInt(c, 10);
    return n >= 35 && n <= 47 && n % 2 === 1;
  }
  // r197 (#353, 20.10.2026): the scale comes from the SKU, not from the Schnitt's display value - "Kragenweite" is
  // "Collar size" on /en, so a collar size picked there landed in 'm:konf'. An Intex SKU is
  // {group}_{colour}_{sizeType}_{size}, and size type 70 is the collar width (live 20.10: 1,577 web variants, all of
  // them men's shirts and shirt-jackets in 37 … 46; the 94085 Kragenweite is 70, its Standardgröße 30). Only when no
  // SKU of the variants in question has that shape (none of the men's pieces today) the old rule decides: a Schnitt
  // named Kragenweite / Collar size, else an odd size between 35 and 47. card-product.liquid keeps the same rule.
  var COLLAR_SIZE_TYPE = '70';
  function sizeTypeOf(sku) {
    var p = String(sku == null ? '' : sku).split('_');
    return p.length === 4 && p[2] && p[3] ? p[2] : null;
  }
  function isCollarScale(sizeValues, cut, skus) {
    var types = (skus || []).map(sizeTypeOf).filter(Boolean);
    if (types.length) return types.indexOf(COLLAR_SIZE_TYPE) !== -1;
    if (cut) return /kragen|collar/i.test(cut);
    return (sizeValues || []).some(oddCollar);
  }
  // sizeValues: every Größe value of the product (of the given Schnitt), sold out ones included;
  // skus: the SKUs of the variants the size is for (the selected Schnitt's, a cart line's, or all of the product's)
  function familyFor(product, sizeValues, cut, skus) {
    if (!product) return 'u:konf';
    var g = genderOf(product.tags), k = kindOf(product.tags, product.type, product.title);
    if (g === 'm' && k === 'konf' && isCollarScale(sizeValues, cut, skus)) k = 'kragen';
    return family(g, k);
  }
  // "48 (LEDERHOSEN)" → { clean: "48", sfx: "lederhosen", sys: "num" }
  function parse(value) {
    var s = String(value == null ? '' : value).trim();
    var m = s.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    var clean = (m ? m[1] : s).trim();
    var sfx = m ? m[2].trim().toLowerCase() : '';
    return { full: s, clean: clean, sfx: sfx, sys: /^\d/.test(clean) ? 'num' : 'alpha' };
  }

  // ── size memory ───────────────────────────────────────────────────────────
  var sizes = {
    all: function () { return readJson(SIZE_KEY); },
    get: function (fam) {
      var a = readJson(SIZE_KEY);
      var e = a[fam] || null;
      // r160: a Damen apron without an apron size of its own follows the remembered
      // Konfektion (Dirndl/Bluse) size through APRON_SIZE; a size the visitor picked on
      // an apron herself always wins. 48/50 have no apron size and derive nothing.
      if (fam === 'f:schuerze' && !(e && e.alpha)) {
        var k = a['f:konf'];
        var alpha = (k && k.num) ? APRON_SIZE[String(k.num)] : null;
        if (alpha) {
          var d = {};
          if (e) { Object.keys(e).forEach(function (key) { d[key] = e[key]; }); }
          d.alpha = alpha; d.alpha_sfx = ''; d.derived = String(k.num); d.t = d.t || k.t;
          e = d;
        }
      }
      return e;
    },
    remember: function (fam, value, at) {
      if (!fam || value == null || value === '') return;
      var p = parse(value);
      if (!p.clean) return;
      // Lederhosen sizes are their own scale — a "(LEDERHOSEN)" pick never feeds the Konfektion family.
      if (p.sfx === 'lederhosen') fam = fam.split(':')[0] + ':lederhose';
      var a = readJson(SIZE_KEY);
      var e = a[fam] || {};
      e[p.sys] = p.clean;
      e[p.sys + '_sfx'] = p.sfx;
      e.t = at || Date.now();
      a[fam] = e;
      writeJson(SIZE_KEY, a);
      // r155: rails that filter by size re-run their pass (PDP size click, cart lines)
      try { document.dispatchEvent(new CustomEvent('personal:size', { detail: { family: fam } })); } catch (err) {}
    },
    // Merge a remote copy (account sync / order history): the newer entry wins per family.
    merge: function (remote) {
      if (!remote || typeof remote !== 'object') return;
      var a = readJson(SIZE_KEY), changed = false;
      Object.keys(remote).forEach(function (fam) {
        var r = remote[fam];
        if (!r || typeof r !== 'object' || !/^[fmku]:[a-z]+$/.test(fam)) return;
        // r192: the account copy and the order history can still file a shirt's collar size under
        // 'm:konf'; an odd one is a collar size for sure (an even one is the worker's to sort out)
        if (fam === 'm:konf' && r.num && oddCollar(r.num) && !r.alpha) fam = 'm:kragen';
        var rt = Number(r.t) || 0, lt = (a[fam] && Number(a[fam].t)) || 0;
        if (rt > lt) { a[fam] = { num: r.num || undefined, alpha: r.alpha || undefined, num_sfx: r.num_sfx || '', alpha_sfx: r.alpha_sfx || '', t: rt }; changed = true; }
      });
      if (changed) writeJson(SIZE_KEY, a);
    },
    // The remembered value that exists in `values` (option values of one product), or null.
    // Exact match first (same suffix), then the same clean size in any suffix.
    match: function (fam, values) {
      var e = sizes.get(fam);
      if (!e || !values || !values.length) return null;
      var want = [];
      if (e.num) want.push({ clean: e.num, sfx: e.num_sfx || '' });
      if (e.alpha) want.push({ clean: e.alpha, sfx: e.alpha_sfx || '' });
      if (!want.length) return null;
      var parsed = values.map(function (v) { return parse(v); });
      for (var i = 0; i < want.length; i++) {
        for (var j = 0; j < parsed.length; j++) {
          if (parsed[j].clean === want[i].clean && parsed[j].sfx === want[i].sfx) return parsed[j].full;
        }
      }
      for (var k = 0; k < want.length; k++) {
        for (var l = 0; l < parsed.length; l++) {
          if (parsed[l].clean === want[k].clean && parsed[l].sfx !== 'lederhosen') return parsed[l].full;
        }
      }
      return null;
    },
    // r155 (SJ 12.09 15:26 "limit any recommendations to items available in the user's
    // size"): does this card come in the remembered size? Cards without a size
    // dimension (scarves, bags, Einheitsgröße) and families without a memory always
    // fit — the filter only ever removes pieces KNOWN not to come in the visitor's size.
    fits: function (card) {
      if (!card || !card.getAttribute) return true;
      var list = card.getAttribute('data-sizes');
      if (!list) return true;
      var fam = family(card.getAttribute('data-gender') || 'u', card.getAttribute('data-kind') || 'konf');
      var e = sizes.get(fam);
      if (!e || (!e.num && !e.alpha)) return true;
      var vals = list.split('|').map(parse);
      // judge only in a size system the memory knows: a remembered 38 says nothing about
      // an apron sold in XS–L, so pieces sized in an unknown system always fit
      var judgeable = vals.some(function (v) { return (v.sys === 'num' && !!e.num) || (v.sys === 'alpha' && !!e.alpha); });
      if (!judgeable) return true;
      return !!sizes.match(fam, list.split('|'));
    },
    familyFor: familyFor,
    family: family,
    parse: parse
  };

  // r192: until now a men's shirt's collar size landed in 'm:konf' next to the suit sizes. Once per browser,
  // a remembered 'm:konf' size between 35 and 47 leaves it: an odd one is a collar size for sure and moves to
  // 'm:kragen', an even one could be either and is forgotten (a wrong preselect is worse than none). The
  // emptied entry keeps a fresh timestamp, so the older account copy cannot bring the size back on merge.
  function splitCollarSizes() {
    try { if (localStorage.getItem(SIZE_SPLIT_KEY)) return; } catch (e) { return; }
    var a = readJson(SIZE_KEY), k = a['m:konf'];
    var c = k && k.num ? parse(k.num).clean : '';
    var n = /^\d+$/.test(c) ? parseInt(c, 10) : 0;
    if (n >= 35 && n <= 47) {
      var now = Date.now();
      if (n % 2 === 1 && !(a['m:kragen'] && a['m:kragen'].num)) a['m:kragen'] = { num: c, num_sfx: '', t: now };
      delete k.num;
      delete k.num_sfx;
      k.t = now;
      writeJson(SIZE_KEY, a);
    }
    try { localStorage.setItem(SIZE_SPLIT_KEY, '1'); } catch (e) {}
  }
  splitCollarSizes();

  // r155 (SJ 12.09 15:25: the „Weil Sie …" rail mixed women's and men's pieces):
  // seed/card gender compatibility — a women's seed never pulls men's pieces and
  // vice versa, unisex cards fit everywhere, an unknown seed gender filters nothing.
  function genderFits(seed, card) {
    if (!seed || seed === 'u' || !card || card === 'u') return true;
    return seed === card;
  }

  // ── wishlist alerts (on-site) ─────────────────────────────────────────────
  // Snapshot per handle at first sight; later renders compare and keep an alert
  // flag for 7 days: price dropped → 'price', was sold out and is back → 'back'.
  var wish = {
    check: function (handle, priceCents, available) {
      if (!handle) return null;
      var snap = readJson(SNAP_KEY);
      var s = snap[handle];
      var p = Number(priceCents), a = !!available, now = Date.now();
      var alert = null;
      if (s) {
        if (s.alert && s.at && (now - s.at) < ALERT_TTL_MS) alert = s.alert;
        if (Number.isFinite(p) && Number.isFinite(Number(s.p)) && p < Number(s.p)) { alert = 'price'; s.at = now; s.p0 = Number(s.p); }
        else if (s.a === false && a) { alert = 'back'; s.at = now; }
        if (alert === 'price' && Number.isFinite(p) && s.p0 != null && p >= s.p0) alert = null; // price went back up
        if (alert === 'back' && !a) alert = null;
        s.alert = alert || undefined;
      } else {
        s = {};
      }
      if (Number.isFinite(p)) s.p = p;
      s.a = a;
      s.t = now;
      snap[handle] = s;
      // keep the snapshot bounded
      var keys = Object.keys(snap);
      if (keys.length > 300) {
        keys.sort(function (x, y) { return (snap[x].t || 0) - (snap[y].t || 0); }).slice(0, keys.length - 300).forEach(function (k) { delete snap[k]; });
      }
      writeJson(SNAP_KEY, snap);
      return alert;
    },
    label: function (alert) { return alert === 'price' ? window.g26t('wishlist.alert_price_drop', 'Preis gesenkt') : (alert === 'back' ? window.g26t('wishlist.alert_back_in_stock', 'Wieder verfügbar') : ''); }
  };

  // ── return visits ─────────────────────────────────────────────────────────
  var last = {
    read: function () { return readJson(LAST_KEY); },
    write: function (v) { writeJson(LAST_KEY, v); },
    isReturnVisit: function () {
      var l = readJson(LAST_KEY);
      var seen = Number(l.seen) || 0;
      return !seen || (Date.now() - seen) > SESSION_GAP_MS;
    },
    touch: function () { var l = readJson(LAST_KEY); l.seen = Date.now(); writeJson(LAST_KEY, l); },
    recordProduct: function (h, title, img, url) {
      if (!h) return;
      var l = readJson(LAST_KEY);
      l.p = { h: h, title: title || '', img: img || '', url: url || window.g26url('products/' + h), t: Date.now() };
      writeJson(LAST_KEY, l);
    },
    recordCollection: function (url, title) {
      if (!url) return;
      var l = readJson(LAST_KEY);
      l.c = { url: url, title: title || '', t: Date.now() };
      writeJson(LAST_KEY, l);
    }
  };

  // ── card decoration ───────────────────────────────────────────────────────
  // „In Ihrer Größe 38" under the price of every card whose product carries the
  // remembered size in stock (card-product.liquid emits data-gender/-kind/-sizes),
  // plus the wishlist alert badge on wishlisted cards. Idempotent per card.
  function wishHandles() {
    try { var a = JSON.parse(localStorage.getItem('goessl_wishlist')) || []; return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  // r155: server-rendered recommendation rails opt in with data-g26-size-filter on
  // their track — pieces that do not come in the remembered size are taken out
  // (kept aside for a re-run), unless fewer than 4 would remain: a sparse rail
  // helps nobody, so such a rail stays complete.
  var SIZE_FILTER_MIN = 4;
  function sizeFilter(track, reset) {
    if (reset && track._g26Removed) {
      track._g26Removed.forEach(function (it) { track.appendChild(it); });
      track._g26Removed = null;
      track.removeAttribute('data-g26-size-filtered');
    }
    if (track.hasAttribute('data-g26-size-filtered')) return;
    track.setAttribute('data-g26-size-filtered', '1');
    var items = Array.prototype.slice.call(track.querySelectorAll('.g26-slider__item'));
    var keep = items.filter(function (it) { var c = it.querySelector('article.goessl-product-card'); return !c || sizes.fits(c); });
    if (keep.length < SIZE_FILTER_MIN || keep.length === items.length) return;
    track._g26Removed = items.filter(function (it) { return keep.indexOf(it) === -1; });
    track._g26Removed.forEach(function (it) { it.remove(); });
    track.dispatchEvent(new Event('scroll'));
  }
  function decorate(root) {
    var scope = root || document;
    var wl = null;
    scope.querySelectorAll('[data-g26-size-filter]').forEach(function (track) { sizeFilter(track, false); });
    scope.querySelectorAll('article.goessl-product-card:not([data-g26-personal])').forEach(function (card) {
      card.setAttribute('data-g26-personal', '1');
      var list = card.getAttribute('data-sizes');
      if (list) {
        var fam = family(card.getAttribute('data-gender') || 'u', card.getAttribute('data-kind') || 'konf');
        var hit = sizes.match(fam, list.split('|'));
        if (hit) {
          var content = card.querySelector('.goessl-product-card__content');
          if (content && !content.querySelector('.goessl-product-card__size-note--mine')) {
            var note = document.createElement('span');
            note.className = 'goessl-product-card__size-note goessl-product-card__size-note--mine';
            note.textContent = window.g26tText('personal.in_your_size', 'In Ihrer Größe {{ size }}', { size: parse(hit).clean });
            content.appendChild(note);
          }
        }
      }
      var handle = card.getAttribute('data-handle');
      if (handle && card.hasAttribute('data-price')) {
        if (wl === null) wl = wishHandles();
        if (wl.indexOf(handle) !== -1) {
          var alert = wish.check(handle, card.getAttribute('data-price'), card.getAttribute('data-available') === 'true');
          if (alert) {
            var badges = card.querySelector('.goessl-product-card__badges');
            if (badges && !badges.querySelector('.goessl-product-card__badge--preorder')) {
              badges.innerHTML = '';
              var b = document.createElement('span');
              b.className = 'goessl-product-card__badge goessl-product-card__badge--alert';
              b.textContent = wish.label(alert);
              badges.appendChild(b);
            }
          }
        }
      }
    });
  }

  // r190: the PDP apron add-ons (assets/product-addons.js) preselect through the same rule - one source
  function apronSize(size) { return APRON_SIZE[String(size == null ? '' : size).trim()] || null; }
  window.GoesslPersonal = { sizes: sizes, wish: wish, last: last, decorate: decorate, genderOf: genderOf, kindOf: kindOf, genderFits: genderFits, apronSize: apronSize };

  // ── page bookkeeping ──────────────────────────────────────────────────────
  function onReady() {
    var shell = document.querySelector('[data-g26-resume]');
    var isReturn = last.isReturnVisit();
    // f) record where the visitor is, then the resume strip on the homepage
    if (shell) {
      var tpl = shell.getAttribute('data-template') || '';
      if (tpl === 'product' && shell.getAttribute('data-handle')) {
        var ph = shell.getAttribute('data-handle'), pt = shell.getAttribute('data-title'), pimg = shell.getAttribute('data-image');
        last.recordProduct(ph, pt, pimg, window.g26url('products/' + ph));
        // 22.10.2026 (SJ: "a blue shirt is clicked, it might go to a yellow shirt"): "Weitermachen" reopens the variant
        // last on screen - the address bar product-form.js keeps - with its colour's picture (the active swatch)
        document.addEventListener('variant:change', function (e) {
          // a colour without a chosen size counts too (sizeOpen, SJ 22.10.2026): the address bar names the colour then
          if (!e.detail || !(e.detail.variant || e.detail.sizeOpen)) return;
          var sw = document.querySelector('.goessl-product__color-swatch.active img');
          last.recordProduct(ph, pt, sw ? sw.getAttribute('src') : pimg, location.pathname + location.search);
        });
      } else if (tpl === 'collection' && shell.getAttribute('data-url')) {
        last.recordCollection(shell.getAttribute('data-url'), shell.getAttribute('data-title'));
      } else if (tpl === 'index' && isReturn) {
        renderResume(shell);
      }
    }
    last.touch();
    decorate(document);
    // late-rendered cards (sliders, search, quick view, the personal band)
    if ('MutationObserver' in window && document.body) {
      var timer = null;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(function () { decorate(document); }, 150);
      }).observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('personal:size', function () {
      document.querySelectorAll('[data-g26-size-filter]').forEach(function (track) { sizeFilter(track, true); });
      document.querySelectorAll('article.goessl-product-card[data-g26-personal]').forEach(function (c) { c.removeAttribute('data-g26-personal'); });
      document.querySelectorAll('.goessl-product-card__size-note--mine').forEach(function (n) { n.remove(); });
      decorate(document);
    });
    document.addEventListener('wishlist:updated', function () {
      document.querySelectorAll('article.goessl-product-card[data-g26-personal]').forEach(function (c) { c.removeAttribute('data-g26-personal'); });
      decorate(document);
    });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderResume(shell) {
    try { if (sessionStorage.getItem('g26_resume_off')) return; } catch (e) {}
    var l = last.read();
    var now = Date.now();
    var items = [];
    if (l.p && l.p.h && (now - (l.p.t || 0)) < LAST_MAX_AGE_MS) {
      items.push('<a class="g26-resume__item" href="' + esc(l.p.url || window.g26url('products/' + l.p.h)) + '">' +
        (l.p.img ? '<img src="' + esc(l.p.img) + '" alt="" width="28" height="36" loading="lazy">' : '') +
        '<span>' + esc(l.p.title || window.g26t('products.recently_viewed.heading_recent', 'Zuletzt angesehen')) + '</span></a>');
    }
    if (l.c && l.c.url && (now - (l.c.t || 0)) < LAST_MAX_AGE_MS) {
      items.push('<a class="g26-resume__item" href="' + esc(l.c.url) + '"><span>' + window.g26t('personal.resume_back_to', 'Zurück zu {{ title }}', { title: esc(l.c.title || window.g26t('personal.resume_your_selection', 'Ihrer Auswahl')) }) + '</span></a>');
    }
    var cartCount = parseInt(shell.getAttribute('data-cart') || '0', 10);
    if (cartCount > 0) {
      items.push('<a class="g26-resume__item" href="' + window.g26url('cart') + '"><span>' + (cartCount === 1 ? window.g26t('personal.resume_cart.one', 'Ihr Warenkorb · {{ count }} Artikel', { count: cartCount }) : window.g26t('personal.resume_cart.other', 'Ihr Warenkorb · {{ count }} Artikel', { count: cartCount })) + '</span></a>');
    }
    if (!items.length) return;
    shell.innerHTML = '<div class="g26-resume__inner">' +
      '<span class="g26-resume__kicker">' + window.g26t('personal.resume_kicker', 'Weitermachen') + '</span>' + items.join('') +
      '<button type="button" class="g26-resume__close" aria-label="' + window.g26t('general.accessibility.dismiss', 'Ausblenden') + '">&times;</button></div>';
    shell.hidden = false;
    var close = shell.querySelector('.g26-resume__close');
    if (close) close.addEventListener('click', function () {
      shell.hidden = true;
      try { sessionStorage.setItem('g26_resume_off', '1'); } catch (e) {}
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady); else onReady();
})();
